import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import API from '../config/axiosConfig';
import {
  Grid, Typography, Checkbox, FormControlLabel, Link
} from '@mui/material';
import CommonLayout from '../components/CommonLayout';
import LoadingSubmitButton from '../components/LoadingSubmitButton';
import InputWithIcon from '../components/InputWithIcon';
import DropdownWithIcon from '../components/DropdownWithIcon';
import userIcon from '../assets/users/user.svg';
import emailIcon from '../assets/users/email.svg';
import phoneIcon from '../assets/users/phone.svg';
import passwordIcon from '../assets/users/password.svg';
import roleIcon from '../assets/users/role.svg';
import { hospitalRoles } from '../config/hospitalRoles';
import { canCompleteEntraInvitation } from '../auth/capabilityPolicy';

function RegisterFromInvitation() {
    const [user, setUser] = useState({
      name: '',
      email: '',
      phone: '',
      password: '',
      hospitalRole: '',
    });
    const [confirmPassword, setConfirmPassword] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [invitationDetails, setInvitationDetails] = useState(null);
    const [error, setError] = useState('');
    const [formErrors, setFormErrors] = useState({});

    const navigate = useNavigate();
    const location = useLocation();
    const { login, loginWithEntra, authCapabilities } = useContext(UserContext);
    const token = new URLSearchParams(location.search).get('token');
    const entraInvitationAvailable = canCompleteEntraInvitation(authCapabilities);

    useEffect(() => {
        if (token) {
            API.get(`/api/users/handle-invitation/${token}`)
                .then(response => {
                    setInvitationDetails(response.data);
                    setUser(u => ({ ...u, email: response.data.email }));
                })
                .catch(err => {
                    setError(err.response?.data?.error?.hebMessage || 'שגיאה באימות ההזמנה');
                });
        }
    }, [token]);

    const validateForm = () => {
      const newErrors = {};
      if (!user.name) {
        newErrors.name = 'שדה חובה';
      }
      if (!user.password) {
        newErrors.password = 'שדה חובה';
      } else if (user.password.length < 6) {
        newErrors.password = 'סיסמה צריכה להכיל מינימום 6 תוים';
      }
      if (!confirmPassword) {
        newErrors.confirmPassword = 'שדה חובה';
      } else if (user.password !== confirmPassword) {
        newErrors.confirmPassword = 'סיסמאות לא תואמות';
      }
      if (!termsAccepted) {
        newErrors.terms = 'חובה לאשר את תנאי השימוש';
      }
      setFormErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleBlur = (field) => {
      if (field === 'confirmPassword' && user.password && confirmPassword && user.password !== confirmPassword) {
        setFormErrors(prev => ({ ...prev, confirmPassword: 'סיסמאות לא תואמות' }));
      } else {
        setFormErrors(prev => ({ ...prev, [field]: '' }));
      }
    };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const userData = { ...user };
      if (!userData.hospitalRole) {
        delete userData.hospitalRole;
      }
      const response = await API.post('/api/users/register-from-invitation', { ...userData, token });
      await login(response.data.token);
      localStorage.setItem('mashlom.termsSigned', Date.now());
      navigate('/protocols');
    } catch (error) {
      console.error('Registration failed:', error);
      setError(error.response?.data?.error?.hebMessage || 'ההרשמה נכשלה');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) {
      return <CommonLayout><Typography color="error">{error}</Typography></CommonLayout>
  }

  if (!invitationDetails) {
      return <CommonLayout><Typography>טוען פרטי הזמנה...</Typography></CommonLayout>
  }

  return (
    <CommonLayout>
        <div className="add-user-container">
            <div className="user-header">
              <Typography variant="h5" style={{fontWeight:"bold", padding: "5px", marginBottom: "20px", textAlign: "center"}}>
                הרשמה ל{invitationDetails.department.nameHebrew} ב{invitationDetails.hospital.nameHebrew}
              </Typography>
            </div>
            {entraInvitationAvailable && (
              <button
                type="button"
                className="btn btn-outline-primary w-100 mb-3"
                onClick={() => loginWithEntra({
                  intent: 'invitation',
                  invitationToken: token,
                  loginHint: invitationDetails.email,
                  prompt: 'create',
                })}
              >
                בדיקת ההזמנה באמצעות Microsoft
              </button>
            )}
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <InputWithIcon
                    icon={userIcon}
                    placeholder="שם"
                    value={user.name}
                    onChange={(e) => {
                      setUser({ ...user, name: e.target.value });
                      setFormErrors(prev => ({ ...prev, name: '' }));
                    }}
                    error={!!formErrors.name}
                  />
                  {formErrors.name && <div className="error-message">{formErrors.name}</div>}
                </Grid>
                <Grid item xs={12}>
                  <InputWithIcon
                    icon={emailIcon}
                    placeholder="אימייל"
                    value={user.email}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputWithIcon
                    icon={passwordIcon}
                    placeholder="סיסמה"
                    type="password"
                    value={user.password}
                    onChange={(e) => {
                      setUser({ ...user, password: e.target.value });
                      setFormErrors(prev => ({ ...prev, password: '' }));
                    }}
                    error={!!formErrors.password}
                  />
                  {formErrors.password && <div className="error-message">{formErrors.password}</div>}
                </Grid>
                <Grid item xs={12}>
                  <InputWithIcon
                    icon={passwordIcon}
                    placeholder="חזור על הסיסמה"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setFormErrors(prev => ({ ...prev, confirmPassword: '' }));
                    }}
                    onBlur={() => handleBlur('confirmPassword')}
                    error={!!formErrors.confirmPassword}
                  />
                  {formErrors.confirmPassword && <div className="error-message">{formErrors.confirmPassword}</div>}
                </Grid>
                <Grid item xs={12}>
                  <InputWithIcon
                    icon={phoneIcon}
                    placeholder="טלפון"
                    value={user.phone}
                    onChange={(e) => setUser({ ...user, phone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DropdownWithIcon
                    icon={roleIcon}
                    placeholder="תפקיד בבית חולים"
                    value={user.hospitalRole || ''}
                    onChange={(e) => setUser({ ...user, hospitalRole: e.target.value })}
                  >
                    {hospitalRoles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </DropdownWithIcon>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    style={{margin: '0px', padding: '0px'}}
                    control={<Checkbox checked={termsAccepted} onChange={(e) => {
                      setTermsAccepted(e.target.checked);
                      setFormErrors(prev => ({ ...prev, terms: '' }));
                    }} />}
                    label={
                      <Typography>
                        אני מאשר\ת כי קראתי את <Link href="https://drive.google.com/file/d/1x8SNHDi9QbS-JYTWLYx7Yz7G68KlhWfM/view" target="_blank" rel="noopener">תנאי השימוש</Link> ואני מסכימ\ה להם
                      </Typography>
                    }
                  />
                  {formErrors.terms && <div className="error-message">{formErrors.terms}</div>}
                </Grid>
                <Grid item xs={12} className="submit-button-container" style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                  <LoadingSubmitButton
                    isLoading={isSubmitting}
                    text="הירשם"
                    loadingText="נרשם..."
                    style={{
                      backgroundColor: '#103C6E',
                      color: 'white',
                      minWidth: '200px',
                      '&:hover': {
                        backgroundColor: '#0d3157',
                      },
                      '&:disabled': {
                        backgroundColor: '#B9EDE7',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </form>
        </div>
    </CommonLayout>
  );
}

export default RegisterFromInvitation;